import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import { PageHeader, NoDomain } from "@/components/Common";
import Glossy3DCard from "@/components/Glossy3DCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Share2, Plus, X, ExternalLink, Lock, Globe, ShieldCheck, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const MODE_LABEL = { DISALLOWED: "Off — only within domain", ALLOWLISTED_DOMAINS: "Allowlisted domains", ALLOWED: "On — any external domain" };
const modeTint = (m) => m === "ALLOWED" ? "bg-emerald-100 text-emerald-700" : m === "ALLOWLISTED_DOMAINS" ? "bg-amber-100 text-amber-700" : m === "DISALLOWED" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-500";

function classifyLive(err) {
  const e = (err || "").toLowerCase();
  const urlMatch = (err || "").match(/https?:\/\/[^\s)]+/);
  if (e.includes("has not been used") || e.includes("service_disabled") || (e.includes("api") && e.includes("disabled"))) {
    return { title: "Cloud Identity API is not enabled", hint: "Enable it in Google Cloud for this project, wait 1–2 minutes for it to propagate, then click refresh.", url: urlMatch ? urlMatch[0] : null, urlLabel: "Enable Cloud Identity API" };
  }
  if (e.includes("unauthorized_client") || e.includes("insufficient") || e.includes("scope") || e.includes("access_denied") || e.includes("permission")) {
    return { title: "Scope not authorized yet", hint: "Add the cloud-identity.policies.readonly scope to this domain's Domain-wide delegation (full list on the Settings page), then refresh.", url: "https://admin.google.com/ac/owl/domainwidedelegation", urlLabel: "Open Domain-wide delegation" };
  }
  return { title: "Couldn't read live settings", hint: err, url: null };
}

const PRESETS = [
  { key: "strict", label: "Strict", desc: "Sharing only within the domain", icon: Lock, tint: "text-rose-600" },
  { key: "moderate", label: "Moderate", desc: "Allowlisted domains only", icon: ShieldCheck, tint: "text-amber-600" },
  { key: "open", label: "Open", desc: "External sharing allowed", icon: Globe, tint: "text-emerald-600" },
];

export default function Sharing() {
  const { selected, refresh } = useDomains();
  const did = selected?.id;
  const [state, setState] = useState("moderate");
  const [allowlist, setAllowlist] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [newTrusted, setNewTrusted] = useState("");

  const addTrusted = async () => {
    const dm = newTrusted.trim().toLowerCase();
    if (!dm) return;
    try { await api.post(`/domains/${selected.id}/sharing/allowlisted-domains`, { domain: dm }); toast.success("Trusted domain added to Google"); setNewTrusted(""); loadLive(selected.id); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeTrusted = async (dm) => {
    try { await api.delete(`/domains/${selected.id}/sharing/allowlisted-domains/${dm}`); toast.success("Trusted domain removed"); loadLive(selected.id); }
    catch (e) { toast.error(apiError(e)); }
  };

  const loadLive = async (id) => {
    setLiveLoading(true); setLive(null);
    try { const { data } = await api.get(`/domains/${id}/sharing/settings`); setLive(data); }
    catch (e) { setLive({ error: apiError(e) }); }
    finally { setLiveLoading(false); }
  };

  useEffect(() => {
    if (selected) {
      setState(selected.sharing_policy?.state || "moderate");
      setAllowlist(selected.sharing_policy?.allowlist || []);
      loadLive(selected.id);
    }
  }, [selected]);

  if (!selected) return <NoDomain />;

  const save = async (nextState, nextList) => {
    try { await api.put(`/domains/${did}/sharing`, { state: nextState ?? state, allowlist: nextList ?? allowlist }); toast.success("Sharing policy saved"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addDomain = () => {
    const d = newDomain.trim().toLowerCase();
    if (!d || allowlist.includes(d)) return;
    const next = [...allowlist, d]; setAllowlist(next); setNewDomain(""); save(state, next);
  };
  const removeDomain = (d) => { const next = allowlist.filter((x) => x !== d); setAllowlist(next); save(state, next); };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Cross-Domain Sharing" subtitle={selected.domain} icon={Share2} />

      <div className="glass mb-4 rounded-3xl p-6" data-testid="live-sharing-card">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-800">Current Google configuration (live)</h3>
            <p className="text-xs text-slate-500">Read directly from this domain's Drive &amp; Docs policy.</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-xl bg-white/60" onClick={() => loadLive(selected.id)} data-testid="refresh-live-sharing"><RefreshCw className="h-4 w-4" /></Button>
        </div>
        {liveLoading ? <div className="flex justify-center p-6 text-emerald-600"><Loader2 className="h-6 w-6 animate-spin" /></div> :
          live?.error ? (() => {
            const c = classifyLive(live.error);
            return (
              <div className="rounded-2xl bg-amber-50 p-4 text-amber-800" data-testid="live-sharing-error">
                <p className="text-sm font-semibold">{c.title}</p>
                <p className="mt-1 break-words text-xs">{c.hint}</p>
                {c.url && (
                  <a href={c.url} target="_blank" rel="noreferrer" data-testid="live-error-cta"
                    className="mt-2 inline-flex items-center gap-1 rounded-lg bg-white/70 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:underline">
                    {c.urlLabel} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            );
          })() : live ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">External sharing mode:</span>
                <Badge className={modeTint(live.external_sharing_mode)} data-testid="live-mode">{MODE_LABEL[live.external_sharing_mode] || live.external_sharing_mode || "Unknown"}</Badge>
              </div>
              <div>
                <p className="mb-1 text-sm text-slate-500">Allowlisted (trusted) domains in Google — add or remove here:</p>
                <div className="mb-2 flex flex-wrap gap-2" data-testid="live-allowlist">
                  {(live.allowlisted_domains || []).length === 0 ? (
                    <span className="text-sm text-slate-400">None yet</span>
                  ) : live.allowlisted_domains.map((d) => (
                    <span key={d} className="flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-sm text-teal-700" data-testid={`live-trusted-${d}`}>
                      {d}<button onClick={() => removeTrusted(d)} className="text-teal-500 hover:text-teal-800" data-testid={`remove-trusted-${d}`}><X className="h-3.5 w-3.5" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={newTrusted} onChange={(e) => setNewTrusted(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTrusted()} placeholder="partner.com" className="max-w-xs" data-testid="trusted-domain-input" />
                  <Button onClick={addTrusted} className="glossy-emerald relative rounded-xl" data-testid="add-trusted-button"><span className="spec-shine" /><Plus className="mr-1 h-4 w-4" />Add to Google</Button>
                </div>
                <p className="mt-1 text-xs text-slate-400">Writes directly to Google's trusted-domains list (needs the cloud-identity.allowlisteddomains scope).</p>
                {(live.allowlisted_domains || []).length === 0 && live.external_sharing_mode === "ALLOWLISTED_DOMAINS" && (
                  <div className="mt-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-700">
                    Sharing is set to <b>Allowlisted domains</b> but the list came back empty. Authorize the scope <span className="font-mono">cloud-identity.allowlisteddomains</span> (this one grants both read <i>and</i> add/remove — the <span className="font-mono">.readonly</span> variant is optional) in this domain's Domain-wide delegation, then refresh.
                    {live.allowlist_error && <div className="mt-1 break-words font-mono text-[10px] text-amber-600">{live.allowlist_error}</div>}
                    <a href="https://admin.google.com/ac/domains/manage" target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-1 font-medium underline" data-testid="trusted-domains-link">Open trusted domains <ExternalLink className="h-3 w-3" /></a>
                  </div>
                )}
                {live.setting_types?.length > 0 && (
                  <details className="mt-2 text-xs text-slate-400">
                    <summary className="cursor-pointer">Policy settings detected ({live.setting_types.length})</summary>
                    <div className="mt-1 font-mono">{live.setting_types.join(", ")}</div>
                  </details>
                )}
              </div>
              {Object.keys(live.flags || {}).length > 0 && (
                <div className="grid gap-2 pt-1 sm:grid-cols-2">
                  {Object.entries(live.flags).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between rounded-xl bg-white/60 px-3 py-1.5 text-xs">
                      <span className="text-slate-600">{k.replace(/_/g, " ")}</span>
                      <Badge className={v ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>{v ? "On" : "Off"}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
      </div>

      <p className="mb-2 px-1 text-sm font-medium text-slate-600">Portal-recorded preference (your intended policy)</p>

      <div className="mb-4 grid gap-4 sm:grid-cols-3">
        {PRESETS.map((p) => (
          <Glossy3DCard key={p.key} testid={`sharing-preset-${p.key}`} className={`cursor-pointer p-5 ${state === p.key ? "ring-2 ring-emerald-400" : ""}`} onClick={() => { setState(p.key); save(p.key, allowlist); }}>
            <span className="spec-shine" />
            <p.icon className={`mb-3 h-6 w-6 ${p.tint}`} />
            <p className="font-heading font-semibold text-slate-800">{p.label}</p>
            <p className="text-xs text-slate-500">{p.desc}</p>
            {state === p.key && <Badge className="mt-3 bg-emerald-100 text-emerald-700">Active</Badge>}
          </Glossy3DCard>
        ))}
      </div>

      <div className="glass rounded-3xl p-6">
        <h3 className="font-heading mb-1 text-lg font-semibold text-slate-800">Allowlisted domains</h3>
        <p className="mb-4 text-sm text-slate-500">Domains your users are permitted to share with (used with the Moderate preset).</p>
        <div className="mb-4 flex gap-2">
          <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addDomain()} placeholder="partner.com" data-testid="allowlist-input" />
          <Button onClick={addDomain} className="glossy-emerald relative rounded-xl" data-testid="add-allowlist-button"><span className="spec-shine" /><Plus className="mr-1 h-4 w-4" />Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {allowlist.length === 0 ? <p className="text-sm text-slate-400">No domains allowlisted yet.</p> :
            allowlist.map((d) => (
              <span key={d} className="flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700" data-testid={`allowlist-domain-${d}`}>
                {d}<button onClick={() => removeDomain(d)} className="text-emerald-500 hover:text-emerald-800"><X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
        </div>

        <div className="mt-6 rounded-2xl bg-amber-50 p-4 text-xs text-amber-700">
          <p className="mb-2 font-semibold">Applying to Google</p>
          Google Workspace's org-level external-sharing switch is a console/policy control with no stable public write API. This portal records your intended policy + allowlist and keeps it in sync across domains. To enforce it in Google, open the Admin console once:
          <a href="https://admin.google.com/ac/apps/drive/sharing" target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-1 font-medium underline">Drive sharing settings <ExternalLink className="h-3 w-3" /></a>
        </div>
      </div>
    </div>
  );
}
